import { AxiosResponse } from "axios";
import { IExternalQuote, IViolation, IQuoteQuestion } from "./ExternalQuote";
import { IVehicle as ExtVeh } from "../classes/ExternalQuote";
import { IOffline } from "../state/interfaces";

export interface IListOption {
    label: string,
    value: string;
}

export interface IVehicleBase {
    id: number,
    year: number,
    make: string,
    model: string,
    vin: string,
    garagingAddress: IAddress | null,
    vehicleUse: string;
}

export interface IVehicle extends IVehicleBase {
    body: string,
    engineSize: string,
    cylinders: number,
    specs: string,
    ownership: string,
    payments: string,
    useYears: string,
    antiTheftOnStar: boolean,
    antiTheftVehicleRecoveryDevice: boolean,
    antiTheftAutomaticDisabling: boolean,
    valueMiles: string,
    mileage: string,
    parkAtHome: string,
    lienholder: string,
    licenseNumber: string,
    antithft: string,
    collSymbol: string,
    compSymbol: string,
    costNewId: string,
    coverages: Array<ICoverage> | null;
}

export const emptyVehicle = {
    id: 0,
    year: 0,
    make: "",
    model: "",
    body: "",
    engineSize: "",
    cylinders: 0,
    specs: "",
    vin: "",
    ownership: "",
    payments: "",
    useYears: "",
    antiTheftOnStar: false,
    antiTheftVehicleRecoveryDevice: false,
    antiTheftAutomaticDisabling: false,
    vehicleUse: "",
    valueMiles: "",
    mileage: "",
    lienholder: "",
    licenseNumber: "",
    parkAtHome: "",
    garagingAddress: null,
    antithft: "",
    collSymbol: "",
    compSymbol: "",
    costNewId: ""
};

export const emptyAddress = {
    street: "",
    city: "",
    apartment: "",
    stateCode: "",
    zipCode: "",
    country: "USA"
}

export interface IUSPSResult {
    street1: string,
    street2: string,
    city: string,
    state: string,
    zip: string;
}

export interface IAddress {
    street: string,
    city: string,
    apartment: string,
    stateCode: string,
    zipCode: string,
    /** Garaging: USA | Mexico (Q7). */
    country?: string;
}

export interface IAddressChangeResult {
    address: IAddress,
    isFull: boolean,
    isValid?: boolean;
    /** True while a USPS zip/address request is in flight. */
    isVerifying?: boolean;
}

export interface IIncidentInfo {
    category: string,
    violation: string,
    violationCode: string,
    violationDate: Date,
    convictionDate: Date | null,
    incidentDescription: string,
    source: ViolationSource
}

export enum ViolationSource {
    ClientOrSystem = 0,
    MVR = 1 << 0,
    CLUE = 1 << 1,
    Claim = 1 << 2,
    Admin = 1 << 3,
    Bridge = 1 << 4,
    DCS = 1 << 6,
    VERISK = 1 << 7
}

export interface IViolationList {
    violationGroups: Array<IViolationGroup>
}

export interface IViolationGroup {
    groupName: string,
    violations: Array<IViolationData>
}

export interface IViolationData {
    text: string,
    value: string
}

export interface IPerson {
    id: number,
    firstName: string,
    lastName: string,
    birthDate: string,
    age: number,
    gender: string,
    address: IAddress | null;
}

export interface IDriverBase extends IPerson {
    licenseNumber: string,
    licenseState: string,
    licenseStateChanged: boolean,
    driverChanged: boolean,
    currentLicenseStatus: string,
    isInsured: boolean,
    isExcluded: boolean,
    fromVerisk: boolean,
    incidents: Array<IIncidentInfo>,
    studentGood: boolean,
    defensiveDriver: boolean;
}

export interface IDriver extends IDriverBase {
    relationshipLevel: string,
    maritalStatus: string,
    education: string,
    employment: string,
    occupation: IDictionaryItem | null,
    militaryBranch: string,
    militaryRank: string,
    hasBAverageOrHigher: string,
    sr22: string,
    physiciansReport: string,
    accidentsViolations: string,
    /** Years licensed — derived from DOB (started driving at 16) for Qualitas quick quote. */
    licenseYears: string;
}

export interface ICurrentInsurance {
    id: number,
    hasInsurance: string,
    currentInsuranceDetails: string,
    beenUninsured: string,
    uninsuredPeriod: string,
    currentInsuranceCompany: "",
    currentInsuranceCompanyPeriod: "",
    currentInsuranceCompanyMonths: "",
    currentBiLimits: "",
    currentPolicyExpirationDate: Date | null,
    effectiveDate: string,
    otherEffectiveDate: Date | null,
    effectiveDateOption: string,
    otherEffectiveDateOptionVisible: boolean,
    otherEffectiveDateOptionSelected: boolean,
    receiveDocumentsElectronically: string;
}

export interface IContactInfo {
    id: number,
    email: string,
    phone: string,
    emailSubscription: boolean,
    phoneMessagesSubscription: boolean;
}

export interface IDictionaryItem {
    id: string,
    name: string;
}

export interface ICoverage {
    name: string,
    code: string,
    limit: string,
    value: string,
    order: number,
    isSelected: boolean,
    canBeDeclined: boolean,
    Ineligible: boolean,
    isDeclined:boolean;
}

export interface IVehicleCoverages {
    vehicleId: number,
    vehicleDescription: string,
    coverages: Array<ICoverage>;
}

export interface ICoverageConfig {
    name: string,
    code: string,
    carrierName: string,
    packageName: string,
    liabilityCoverages : Array<ICoverage>,
    vehicleCoverages: Array<IVehicleCoverages>,
    addonCoverages: Array<ICoverage>,
}

export interface IOfferErrors {
    code : string,
    domain : string,
    domainKey : string,
    text : string
}

export interface IRate {
    rates: Array<IRealTimeAutoResult>,
    paymentChoices: Array<IPaymentChoice>,
    isError: boolean,
    errorCd: string,
    msgStatusCd: string,
    msgStatusDesc: string,
    agenciesContactInfo: IAgenciesContactInfo,
    offerErrors: Array<IOfferErrors>;
}

export interface IPaymentChoice {
    program: string,
    urlMemo: string,
    payTerm: string,
    payTermCd: string;
    payPlanDesc: string,
    payCount: number,
    installmentDueDt: string,
    firstPay: number,
    regularPay: number,
    motorclubFee: number,
    iFee: number,
    firstPaymentFees: Array<ICoverageResult>,
    payTotal: number,
    selected: boolean,
    methodPaymentCd: string,
    order: number,
    urlMemoTkn: string,
    autopay: boolean;
}

export interface IAgenciesContactInfo {
    callCenterPhone: string,
    agencyContacts: Array<IAgencyContacts>
}

export interface IAgencyContacts {
    agencyName: string,
    agencyPhone: string,
    agencyStreetAddress: string,
    producerCode: string,
    storeNumber: string,
    agencyWebsiteURL: string;
}

export interface IRealTimeAutoResult {
    program: string,
    messageMemo: string,
    urlMemo: string,
    urlLogo: string,
    vehicleRates: Array<IVehicleResult>,
    isError: boolean
}

export interface IVehicleResult {
    year: string,
    make: string,
    model: string,
    total: string,
    liabilityCoverages: Array<ICoverageResult>,
    fees: Array<ICoverageResult>,
    vehicleCoverages: Array<ICoverageResult>,
    addonCoverages: Array<ICoverageResult>
}

export interface ICoverageResult {
    code: string,
    title: string,
    limit: string,
    amt: string,
}

export interface IPaymentInfo {
    instrumentType: string,
    billingAddress: IAddress | null;
}

export interface ICreditCardInfo extends IPaymentInfo {
    cardHolderFirstName: string,
    cardHolderLastName: string,
    cardExpirationFull: string,
    cardExpirationMonth: number,
    cardExpirationYear: number,
    cardNumber: string;
}

export interface IEftInfo extends IPaymentInfo {
    bankName: string,
    transitNumber: string,
    accountNumber: string
}

export interface IPaymentInfoChangeResult {
    paymentInstrument: IPaymentInfo,
    isValid?: boolean;
}

export interface IPaymentBag {
    downPaymentInstrument: IPaymentInfo,
    installmentInstrument: IPaymentInfo
}

export interface IToolTipOption {
    title: string,
    content: string
}

export interface IDiscountOption {
    installments: boolean,
    homeOwner: boolean,
    paperless: boolean,
    priorInsurance: boolean,
    insuredIsDefensiveDriver: boolean,
    militaryDiscount: boolean,
    firstResponders: boolean,
    zeroOrOneViolationLast3Years: boolean
}

export interface IQuoteApiClient {
    createQuote: (program: string, zipCode: string, city: string, stateCode: string, vehicle?: IVehicle | null) => Promise<AxiosResponse<any>>,
    updateQuickQuote: (quoteUid: string, vehicles: Array<IVehicle>, drivers: Array<IDriver>, coverageConfig: ICoverageConfig, discounts: IDiscountOption, contactInfo: IContactInfo, isNonOwners: boolean, quickStep: string) => Promise<AxiosResponse<any>>,
    postCreateQuote: () => Promise<AxiosResponse<any>>,
    updateContactInfo: (quoteUid: string, contactInfo: IContactInfo, quoteStep: string) => Promise<AxiosResponse<any>>,
    getOccupations: (query: string) => Promise<AxiosResponse<any>>,
    getQuote: (quoteUid: string) => Promise<AxiosResponse<any>>
    getContactInfo: (quoteUid: string) => Promise<AxiosResponse<any>>,
    updateQuoteAgency: (quoteUid: string, producerCode: string, storeNumber: string) => Promise<AxiosResponse<any>>,
    getCoverageConfigs: (quoteUid: string) => Promise<AxiosResponse<any>>,
    getRates: (quoteUid: string, coverageConfig: ICoverageConfig) => Promise<AxiosResponse<any>>,
    getSubRates: () => Promise<AxiosResponse<any>>,
    updateDriver: (quoteUid: string, driver: IDriver, quoteStep: string, stateCode: string | null) => Promise<AxiosResponse<any>>,
    updateInsurance: (quoteUid: string, currentInsurance: any, quoteStep: string) => Promise<AxiosResponse<any>>,
    getInsuranceCompanies: () => Promise<AxiosResponse<any>>,
    getVehicleMakes: (year: number, quoteId: number, quoteUid: string) => Promise<AxiosResponse<any>>,
    getVehicleModels: (year: number, make: string, quoteUid: string) => Promise<AxiosResponse<any>>,
    getVinRecords: (year: number, make: string, model: string, quoteUid: string, vin: string | null) => Promise<AxiosResponse<any>>,
    updateVehicle: (quoteUid: string, vehicle: IVehicle, quoteStep: string) => Promise<AxiosResponse<any>>,
    getOfflineData: () => Promise<AxiosResponse<any>>
}

export interface ILOBApiClient {
    validateQuotePostData: () => Promise<AxiosResponse<any>>,
    postCreateQuote: (program: string) => Promise<AxiosResponse<any>>,
    createtQuote: (selectedRate: IRealTimeAutoResult) => Promise<AxiosResponse<any>>,
    getQuote: (quoteId: number) => Promise<AxiosResponse<IExternalQuote>>,
    getLienholders: (query: string) => Promise<AxiosResponse<any>>,
    updateVehicle: (quoteId: number, paymentPlanCode: number, vehicle: ExtVeh, updateLienholder: boolean, updateGaragingAddress: boolean, addressSynchronization: boolean, stateCode: string | null) => Promise<AxiosResponse<any>>,
    getCoverages: (quoteId: number, coverageConfig: ICoverageConfig) => Promise<AxiosResponse<any>>,
    updateCoverages: (quoteId: number, paymentPlanCode: number, coverageConfig: ICoverageConfig) => Promise<AxiosResponse<any>>,
    updateDriver: (quoteId: number, paymentPlanCode: number, driver: IDriver) => Promise<AxiosResponse<any>>,
    savePaymentInfo: (quoteId: number, methodPaymentCd: string, paymentInfoBag: IPaymentBag) => Promise<AxiosResponse<any>>,
    getViolations: (quoteId: number) => Promise<AxiosResponse<any>>,
    getViolationList: (state: string) => Promise<AxiosResponse<IViolationList>>,
    getDefaultMilesByZip: (zip: string, country?: string) => Promise<AxiosResponse<{ miles: number | null }>>,
    reRateQuote: (quoteId: number, paymentPlanCode: number) => Promise<AxiosResponse<any>>,
    reSetQuoteEffDate: (quoteId: number, paymentPlanCode: number, effectiveDate: string) => Promise<AxiosResponse<any>>,
    getPaymentInfo: (quoteId: number) => Promise<AxiosResponse<any>>,
    disputeViolations: (quoteId: number, driverId: number, violationsForDispute: Array<IViolation>) => Promise<AxiosResponse<any>>,
    bindQuote: (quoteId: number) => Promise<AxiosResponse<any>>,
    getDocuments: (quoteId: number) => Promise<AxiosResponse<any>>,
    getVehicle: (quoteId: number, vehicleId: number) => Promise<AxiosResponse<any>>,
    getQuoteQuestions: (rtrQuoteId: number) => Promise<AxiosResponse<any>>,
    updateQuoteQuestions: (quoteId: number, questions: Array<IQuoteQuestion>) => Promise<AxiosResponse<any>>,
    getESignatureForms: (rtrQuoteId: number) => Promise<AxiosResponse<any>>,
    updateESignatureAgreement: (rtrQuoteId: number, esignatureCode: string, insuredName: string, needToSign: boolean, lastForm: boolean, options: any[]) => Promise<AxiosResponse<any>>,
    updateContactInfo: (rtrQuoteId: number, contactInfo: IContactInfo) => Promise<AxiosResponse<any>>,
    updateApplicantMailingAddress: (quoteId: number, applicant: IPerson, contactInfo: IContactInfo) => Promise<AxiosResponse<any>>,
    updateAdditionalDrivers: (quoteId: number, paymentPlanCode: number, additionalDrivers: Array<IDriver>) => Promise<AxiosResponse<any>>,
    createExternalQuote: (tkn: string) => Promise<AxiosResponse<any>>
}

export interface IPaths {
    START: string,
    CHANGE_ZIP: string,
    QUICK: string,
    QUICK_ZIP: string,
    QUICK_VEHICLE: string,
    QUICK_DRIVER: string,
    QUICK_DISCOUNT: string,
    VEHICLE_YMM: string,
    VEHICLE_DETAILS: string,
    HOW_YOU_DRIVE: string,
    ABOUT_YOU: string,
    EDUCATION_EMPLOYMENT: string,
    YOUR_HOME: string,
    YOUR_LICENSE: string,
    DRIVING_HISTORY: string,
    INCIDENTS: string,
    CURRENT_INSURANCE: string,
    INSURANCE_HISTORY: string,
    NEW_POLICY: string,
    CONTACT_INFO: string,
    COVERAGES: string,
    RESULT: string,
    CHECKOUT: string,
    AGENCIES: string,
    FQ_YOUR_HOME: string,
    FQ_CARS: string,
    FQ_DRIVERS: string,
    FQ_COVERAGES: string,
    FQ_POLICY_DETAILS: string,
    FQ_QUESTIONS: string,
    FQ_FINALIZE: string,
    FQ_PAYMENT: string,
    FQ_OFFLINE: string,
    FQ_ADDITIONAL_DRIVERS: string,
    FQ_VIOLATIONS: string,
    FQ_FINISH: string,
    FQ_DOCUMENTS: string,
    FQ_GOODTOGO: string,
    FQ_ESIGNATURE: string,
    RTR_CHECKOUT: string,
    AGENT_LANDING_QUOTE: string,
    BUY_NOW_QUOTE: string,
    AGENT_WILL_CALL: string,
    QUOTE_THANK_YOU: string
    }